// Servicio Firestore para Vortex Academy.
// A partir de aquí, el contenido de la academia (páginas de lectura, y a futuro
// cuestionarios, libros, temas, etc.) se guarda en FIRESTORE para no sobrecargar RTDB.
//
// Estructura:
//   vortexAcademy/{jerarquia}/cuerpos/{cuerpo}/especialidades/{especialidad}/temas/{temaId}
//   .../temas/{temaId}/cuestionarios/{quizId}   ← exámenes (cuestionarios importados)
// Los segmentos de la ruta usan los NOMBRES de app_metadata (los mismos que guarda el
// perfil del alumno en users/{uid}), así admin y alumno siempre coinciden.
//
// Lectura: usuarios autenticados. Escritura: solo admins (colección admins/{uid}
// o custom claim admin). Ver firestore.rules.
import { get, ref } from 'firebase/database';
import {
  collection, doc, getDocs, getDoc, onSnapshot, setDoc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { database, db } from '../firebase';
import vortexDocsService from './vortexDocsService';
import { createBidirectionalMap } from '../constants/specialtyMetadata';
import { AREA_KEYS, AREA_NAMES, areaKeyForId, areaKeyForCategory } from '../constants/exaniAreas';
import { resolveCourseResumen, localCourseResumen } from '../utils/courseStructure';

const NEST = {
  cuerpos: 'cuerpos',
  especialidades: 'especialidades',
  temas: 'temas',
};

const temasCollection = (jerarquia, cuerpo, especialidad) =>
  collection(db, 'vortexAcademy', jerarquia, NEST.cuerpos, cuerpo, NEST.especialidades, especialidad, NEST.temas);

const temaDoc = (jerarquia, cuerpo, especialidad, temaId) =>
  doc(db, 'vortexAcademy', jerarquia, NEST.cuerpos, cuerpo, NEST.especialidades, especialidad, NEST.temas, temaId);

const cuestionariosCollection = (jerarquia, cuerpo, especialidad, temaId) =>
  collection(db, 'vortexAcademy', jerarquia, NEST.cuerpos, cuerpo, NEST.especialidades, especialidad, NEST.temas, temaId, 'cuestionarios');

const cuestionarioDoc = (jerarquia, cuerpo, especialidad, temaId, quizId) =>
  doc(db, 'vortexAcademy', jerarquia, NEST.cuerpos, cuerpo, NEST.especialidades, especialidad, NEST.temas, temaId, 'cuestionarios', quizId);

// Firestore no persiste arreglos vacíos si el documento no los trae: siempre normalizamos.
export const emptyLesson = () => ({
  id: `lt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  title: '',
  module: '',
  paragraphs: [],
  bullets: [],
  callout: null,
  kps: [],
  quizQuestions: [],
  flashcards: [],
  video_url: '',
  audio_url: '',
  audio_caption: '',
  infographic_url: '',
  slides_url: '',
  report_url: '',
  images: [],
  pdf_resources: [],
  interactive: false,
  isQuiz: false,
});

export const normalizeLesson = (lesson = {}) => ({
  id: lesson.id || `lt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  title: lesson.title || '',
  module: lesson.module || '',
  paragraphs: Array.isArray(lesson.paragraphs) ? lesson.paragraphs : [],
  bullets: Array.isArray(lesson.bullets) ? lesson.bullets : [],
  callout: lesson.callout || null,
  kps: Array.isArray(lesson.kps) ? lesson.kps : [],
  quizQuestions: Array.isArray(lesson.quizQuestions) ? lesson.quizQuestions : [],
  flashcards: Array.isArray(lesson.flashcards) ? lesson.flashcards : [],
  video_url: lesson.video_url || '',
  audio_url: lesson.audio_url || '',
  audio_caption: lesson.audio_caption || '',
  infographic_url: lesson.infographic_url || '',
  slides_url: lesson.slides_url || '',
  report_url: lesson.report_url || '',
  images: Array.isArray(lesson.images) ? lesson.images : [],
  pdf_resources: Array.isArray(lesson.pdf_resources) ? lesson.pdf_resources : [],
  interactive: !!lesson.interactive,
  isQuiz: !!lesson.isQuiz,
});

export const emptyPdfResource = () => ({
  id: `pdf_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  name: '',
  url: '',
});

export const toLessonList = (node) => {
  const raw = node?.lessons;
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : Object.values(raw);
  const seen = new Set();
  return list.map((l) => {
    const norm = normalizeLesson(l);
    let id = norm.id;
    while (seen.has(id)) id = `${norm.id}_${Math.random().toString(36).slice(2, 7)}`;
    seen.add(id);
    return { ...norm, id };
  });
};

const mapTemaDoc = (docSnap) => {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    title: data.title || docSnap.id,
    module: data.module || '',
    lessons: toLessonList(data),
    cuestionariosCount: data.cuestionariosCount || 0,
    multimedia: data.multimedia || null,
  };
};

// Estructura de la academia (jerarquías, cuerpos/servicios, especialidades).
// Se lee una sola vez desde app_metadata (RTDB) — nodo pequeño de metadatos, no el banco de preguntas.
export const getAcademyStructure = async () => {
  const snap = await get(ref(database, 'app_metadata'));
  if (!snap.exists()) return { jerarquias: [], cuerpos: [], especialidadesByCuerpo: {} };
  const data = snap.val();
  return {
    jerarquias: data.grados ? Object.values(data.grados) : [],
    cuerpos: data.cuerpos_y_servicios ? Object.values(data.cuerpos_y_servicios) : [],
    especialidadesByCuerpo: data.especialidades || {},
  };
};

// Páginas de lectura de una especialidad (Firestore).
export const watchTemas = ({ jerarquia, cuerpo, especialidad }, cb) => {
  return onSnapshot(
    temasCollection(jerarquia, cuerpo, especialidad),
    (snap) => cb(snap.docs.map(mapTemaDoc)),
    (err) => console.error('[AcademyFirestore] Error leyendo temas:', err)
  );
};

export const getTemas = async ({ jerarquia, cuerpo, especialidad }) => {
  const snap = await getDocs(temasCollection(jerarquia, cuerpo, especialidad));
  return snap.docs.map(mapTemaDoc);
};

export const saveTema = async ({ jerarquia, cuerpo, especialidad, temaId }, tema) => {
  await setDoc(temaDoc(jerarquia, cuerpo, especialidad, temaId), {
    title: tema.title || '',
    module: tema.module || '',
    lessons: Array.isArray(tema.lessons) ? tema.lessons : [],
    multimedia: tema.multimedia || null,
    updatedAt: serverTimestamp(),
  });
};

export const deleteTema = async ({ jerarquia, cuerpo, especialidad, temaId }) => {
  await deleteDoc(temaDoc(jerarquia, cuerpo, especialidad, temaId));
};

// ── Cuestionarios (exámenes) por tema ──
export const getCuestionarios = async ({ jerarquia, cuerpo, especialidad, temaId }) => {
  const snap = await getDocs(cuestionariosCollection(jerarquia, cuerpo, especialidad, temaId));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const watchCuestionarios = ({ jerarquia, cuerpo, especialidad, temaId }, cb) => {
  return onSnapshot(
    cuestionariosCollection(jerarquia, cuerpo, especialidad, temaId),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => console.error('[AcademyFirestore] Error leyendo cuestionarios:', err)
  );
};

export const saveCuestionario = async ({ jerarquia, cuerpo, especialidad, temaId }, quiz) => {
  if (!quiz || !quiz.id) throw new Error('El cuestionario requiere id');
  await setDoc(cuestionarioDoc(jerarquia, cuerpo, especialidad, temaId, quiz.id), {
    ...quiz,
    updatedAt: serverTimestamp(),
  });
};

// Convierte un cuestionario RTDB (preguntas/respuestas/correcta) al formato Vortex Academy.
export const convertCuestionarioRtdb = (quizId, quizzData = {}) => {
  const rawPreguntas = quizzData.preguntas || quizzData.questions || [];
  const preguntas = Array.isArray(rawPreguntas)
    ? rawPreguntas
    : Object.entries(rawPreguntas).map(([id, p]) => ({ id, ...p }));
  const questions = preguntas
    .filter(Boolean)
    .map((p, idx) => {
      const rawResp = Array.isArray(p.respuestas) ? p.respuestas : Object.values(p.respuestas || {});
      const respuestas = rawResp.filter(Boolean).map((r) => (typeof r === 'string' ? { respuesta: r } : r));
      const options = respuestas.map((r) => r.respuesta || r.texto || '');
      const correctaIdx = respuestas.findIndex((r) => r.correcta === true || r.correct === true);
      return {
        id: p.id || idx,
        question: p.pregunta || p.question || '',
        options,
        correct_index: correctaIdx >= 0 ? correctaIdx : 0,
        explanation: p.explicacion || p.explanation || '',
      };
    })
    .filter((q) => q.question && q.options.length > 1);
  return {
    id: quizId,
    title: quizzData.titulo || quizzData.nombre || quizzData.title || 'Cuestionario del tema',
    description: quizzData.descripcion || quizzData.description || '',
    type: quizzData.type || 'examen',
    questions,
  };
};

// ────────────────────────────────────────────────────────────────────────────
// ÁREAS DEL EXANI-II (transversales: Pensamiento Matemático, Comprensión
// Lectora, Redacción Indirecta e Inglés; y los módulos disciplinares).
//
// En RTDB estos temas viven en el nodo `temas` (con area_id 1..22) y sus
// preguntas en `preguntas_por_tema/{temaId}` (formato array u objeto). Cada
// tema define en `grados` qué jerarquías lo ven.
//
// Estructura Firestore:
//   vortexAcademyAreas/{areaKey}/temas/{temaId}
//   .../temas/{temaId}/cuestionarios/{quizId}
// El doc del tema guarda `grados` (nombres de jerarquías asignadas) para que el
// alumno solo vea los temas de su jerarquía y el admin pueda asignarlas.
// ────────────────────────────────────────────────────────────────────────────

// ÁREAS DEL EXANI-II (CENEVAL): pensamiento matemático, comprensión lectora,
// redacción indirecta, inglés (transversales) y los módulos disciplinares.
// La definición canónica vive en `src/constants/exaniAreas.js` y se re-exporta
// aquí por compatibilidad con el resto de la app.
export { AREA_KEYS, AREA_NAMES, areaKeyForId, areaKeyForCategory };

// Conteo real del contenido de un área en Firestore:
// temas, lecciones, mini exámenes (cuestionarios) y preguntas.
export const getAreaStats = async (areaKey) => {
  if (!areaKey) return null;
  const temas = await getAreaTemas(areaKey);
  let lecciones = 0;
  let miniExamenes = 0;
  let preguntas = 0;
  const temasDetail = [];
  for (const tema of temas) {
    const temaLessons = (tema.lessons || []).length;
    const quizzes = await getAreaCuestionarios(areaKey, tema.id);
    lecciones += temaLessons;
    miniExamenes += quizzes.length;
    preguntas += quizzes.reduce((acc, q) => acc + (q.questions?.length || 0), 0);
    temasDetail.push({
      id: tema.id,
      title: tema.title,
      lecciones: temaLessons,
      miniExamenes: quizzes.length,
    });
  }
  return {
    areaKey,
    temas: temas.length,
    lecciones,
    miniExamenes,
    preguntas,
    temasDetail,
    computedAt: Date.now(),
  };
};

// ── Fallback estático (hardcode) ──
// Conteos reales por área del EXANI-II leídos desde la RTDB (nodos `areas`,
// `temas` y `preguntas_por_tema`). Se usan como respaldo mientras Firestore
// (vortexAcademyAreas) no esté poblado o las reglas bloqueen la lectura.
// Cuando getAreaStats() puede leer Firestore, este mapa se ignora.
const FALLBACK_AREA_STATS = {
  pensamiento_matematico: { areaKey: 'pensamiento_matematico', temas: 6, lecciones: 0, miniExamenes: 121, preguntas: 2419, temasDetail: [] },
  comprension_lectora: { areaKey: 'comprension_lectora', temas: 6, lecciones: 0, miniExamenes: 2, preguntas: 21, temasDetail: [] },
  redaccion_indirecta: { areaKey: 'redaccion_indirecta', temas: 6, lecciones: 0, miniExamenes: 1, preguntas: 15, temasDetail: [] },
  ingles: { areaKey: 'ingles', temas: 2, lecciones: 0, miniExamenes: 1, preguntas: 6, temasDetail: [] },
  administracion: { areaKey: 'administracion', temas: 9, lecciones: 0, miniExamenes: 1, preguntas: 6, temasDetail: [] },
  aritmetica: { areaKey: 'aritmetica', temas: 17, lecciones: 0, miniExamenes: 0, preguntas: 0, temasDetail: [] },
  biologia: { areaKey: 'biologia', temas: 23, lecciones: 0, miniExamenes: 0, preguntas: 0, temasDetail: [] },
  calculo: { areaKey: 'calculo', temas: 6, lecciones: 0, miniExamenes: 1, preguntas: 6, temasDetail: [] },
  ciencias_salud: { areaKey: 'ciencias_salud', temas: 20, lecciones: 0, miniExamenes: 1, preguntas: 6, temasDetail: [] },
  derecho: { areaKey: 'derecho', temas: 17, lecciones: 0, miniExamenes: 0, preguntas: 0, temasDetail: [] },
  economia: { areaKey: 'economia', temas: 19, lecciones: 0, miniExamenes: 0, preguntas: 0, temasDetail: [] },
  filosofia: { areaKey: 'filosofia', temas: 24, lecciones: 0, miniExamenes: 0, preguntas: 0, temasDetail: [] },
  fisica: { areaKey: 'fisica', temas: 13, lecciones: 0, miniExamenes: 1, preguntas: 6, temasDetail: [] },
  historia: { areaKey: 'historia', temas: 19, lecciones: 0, miniExamenes: 0, preguntas: 0, temasDetail: [] },
  literatura: { areaKey: 'literatura', temas: 12, lecciones: 0, miniExamenes: 0, preguntas: 0, temasDetail: [] },
  matematicas_financieras: { areaKey: 'matematicas_financieras', temas: 12, lecciones: 0, miniExamenes: 0, preguntas: 0, temasDetail: [] },
  premedicina: { areaKey: 'premedicina', temas: 21, lecciones: 0, miniExamenes: 1, preguntas: 6, temasDetail: [] },
  probabilidad_estadistica: { areaKey: 'probabilidad_estadistica', temas: 13, lecciones: 0, miniExamenes: 0, preguntas: 0, temasDetail: [] },
  psicologia: { areaKey: 'psicologia', temas: 19, lecciones: 0, miniExamenes: 0, preguntas: 0, temasDetail: [] },
  quimica: { areaKey: 'quimica', temas: 13, lecciones: 0, miniExamenes: 0, preguntas: 0, temasDetail: [] },
  ciencias_experimentales: { areaKey: 'ciencias_experimentales', temas: 12, lecciones: 0, miniExamenes: 2, preguntas: 36, temasDetail: [] },
  ciencias_sociales: { areaKey: 'ciencias_sociales', temas: 12, lecciones: 0, miniExamenes: 2, preguntas: 36, temasDetail: [] },
};

// Enriquece un curso de RTDB con su resumen real calculado desde Firestore
// (o el override manual del admin si existe). No modifica la RTDB.
// Si la lectura de Firestore falla (p. ej. reglas que bloquean a usuarios no
// autenticados), usa los conteos estáticos de FALLBACK_AREA_STATS.
export const enrichCourseWithResumen = async (course) => {
  if (!course) return course;
  const areaKey = areaKeyForCategory(course.category);
  if (!areaKey) return { ...course, resumen: localCourseResumen(course) };
  const preservedTemas = Array.isArray(course.areaTemas) ? course.areaTemas : [];
  try {
    const stats = await getAreaStats(areaKey);
    return {
      ...course,
      areaKey,
      areaTemas: stats.temasDetail?.length ? stats.temasDetail : preservedTemas,
      resumen: resolveCourseResumen(course, stats.temasDetail?.length ? stats : null),
    };
  } catch (e) {
    const fallback = FALLBACK_AREA_STATS[areaKey];
    if (fallback) {
      return {
        ...course,
        areaKey,
        areaTemas: fallback.temasDetail?.length ? fallback.temasDetail : preservedTemas,
        resumen: resolveCourseResumen(course, fallback.temasDetail?.length ? fallback : null),
      };
    }
    console.warn('[AcademyFirestore] Error resumen curso:', course.id, e);
    return { ...course, resumen: localCourseResumen(course) };
  }
};

const areaTemasCollection = (areaKey) => collection(db, 'vortexAcademyAreas', areaKey, 'temas');

const areaTemaDoc = (areaKey, temaId) => doc(db, 'vortexAcademyAreas', areaKey, 'temas', temaId);

const areaCuestionariosCollection = (areaKey, temaId) =>
  collection(db, 'vortexAcademyAreas', areaKey, 'temas', temaId, 'cuestionarios');

const areaCuestionarioDoc = (areaKey, temaId, quizId) =>
  doc(db, 'vortexAcademyAreas', areaKey, 'temas', temaId, 'cuestionarios', quizId);

const mapAreaTemaDoc = (docSnap) => {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    title: data.title || docSnap.id,
    module: data.module || '',
    areaId: data.areaId || null,
    grados: Array.isArray(data.grados) ? data.grados : [],
    lessons: toLessonList(data),
    cuestionariosCount: data.cuestionariosCount || 0,
    multimedia: data.multimedia || null,
  };
};

export const watchAreaTemas = (areaKey, cb) => {
  return onSnapshot(
    areaTemasCollection(areaKey),
    (snap) => cb(snap.docs.map(mapAreaTemaDoc)),
    (err) => console.error('[AcademyFirestore] Error leyendo temas de área:', err)
  );
};

export const getAreaTemas = async (areaKey) => {
  const snap = await getDocs(areaTemasCollection(areaKey));
  return snap.docs.map(mapAreaTemaDoc);
};

export const saveAreaTema = async ({ areaKey, temaId }, tema) => {
  await setDoc(areaTemaDoc(areaKey, temaId), {
    title: tema.title || '',
    module: tema.module || '',
    areaId: tema.areaId || areaKeyForId(areaKey),
    grados: Array.isArray(tema.grados) ? tema.grados : [],
    lessons: Array.isArray(tema.lessons) ? tema.lessons : [],
    multimedia: tema.multimedia || null,
    updatedAt: serverTimestamp(),
  });
};

export const deleteAreaTema = async ({ areaKey, temaId }) => {
  await deleteDoc(areaTemaDoc(areaKey, temaId));
};

export const getAreaCuestionarios = async (areaKey, temaId) => {
  const snap = await getDocs(areaCuestionariosCollection(areaKey, temaId));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const watchAreaCuestionarios = (areaKey, temaId, cb) => {
  return onSnapshot(
    areaCuestionariosCollection(areaKey, temaId),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => console.error('[AcademyFirestore] Error leyendo cuestionarios de área:', err)
  );
};

export const saveAreaCuestionario = async ({ areaKey, temaId }, quiz) => {
  if (!quiz || !quiz.id) throw new Error('El cuestionario requiere id');
  await setDoc(areaCuestionarioDoc(areaKey, temaId, quiz.id), {
    ...quiz,
    updatedAt: serverTimestamp(),
  });
};

// Convierte una pregunta del nodo RTDB `preguntas_por_tema` (formato
// {pregunta, respuestas: {id:{respuesta, correcta}}, explicacion})
// al formato Vortex Academy {question, options, correct_index, explanation}.
export const convertPreguntaPorTema = (q = {}) => {
  const respuestas = Array.isArray(q.respuestas)
    ? q.respuestas.filter(Boolean)
    : Object.values(q.respuestas || {}).filter(Boolean);
  const options = respuestas.map((r) => r.respuesta || r.texto || '');
  const correctIdx = respuestas.findIndex((r) => r.correcta === true || r.correct === true);
  return {
    id: q.id,
    question: q.pregunta || q.question || '',
    options,
    correct_index: correctIdx >= 0 ? correctIdx : 0,
    explanation: q.explicacion || q.explanation || '',
  };
};

// Lee el banco de preguntas RTDB de un tema general (`preguntas_por_tema/{temaId}`)
// y lo convierte al formato Vortex Academy. NO es por especialidad: es el banco
// de los temas 1 al 25 que aplica a todas las jerarquías.
export const getPreguntasPorTema = async (temaId) => {
  if (!temaId) return [];
  const snap = await get(ref(database, `preguntas_por_tema/${temaId}`));
  if (!snap.exists()) return [];
  const rawQs = snap.val();
  const qList = Array.isArray(rawQs)
    ? rawQs.filter(Boolean)
    : Object.values(rawQs || {}).filter(Boolean);
  return qList
    .map(convertPreguntaPorTema)
    .filter((q) => q.question && q.options.length > 1);
};

// Baraja las preguntas (y las opciones de cada una) y devuelve `count` al azar.
// Convierte `correct_index` → `correctIndex` y garantiza `id` único para el ExamFinal.
export const sampleFinalQuestions = (questions, count = 20) => {
  const arr = [...(questions || [])];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr.slice(0, count).map((q, idx) => {
    const options = [...(q.options || [])];
    const correctVal = options[q.correct_index];
    for (let i = options.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [options[i], options[j]] = [options[j], options[i]];
    }
    return {
      id: q.id != null ? String(q.id) : `q_${idx}`,
      question: q.question,
      options,
      correctIndex: options.indexOf(correctVal),
      explanation: q.explanation || '',
    };
  });
};

// Importa los temas generales (áreas 1, 2 y 3) de RTDB a Firestore:
// lee `temas` (metadatos: nombre, area_id, grados) y `preguntas_por_tema`
// (banco de preguntas por tema). Cada tema se guarda en
// vortexAcademyAreas/{areaKey}/temas/{temaId} y sus preguntas se reparten en
// cuestionarios de ≤20 preguntas para exámenes manejables.
export const importAreasFromRTDB = async ({ onProgress } = {}) => {
  const [areasSnap, temasSnap, preguntasSnap] = await Promise.all([
    get(ref(database, 'areas')),
    get(ref(database, 'temas')),
    get(ref(database, 'preguntas_por_tema')),
  ]);

  const rawTemas = temasSnap.exists() ? temasSnap.val() : null;
  if (!rawTemas) throw new Error('No hay datos en el nodo "temas" de la RTDB');

  const temas = (Array.isArray(rawTemas) ? rawTemas : Object.values(rawTemas))
    .filter(Boolean)
    .filter((t) => t && t.id);
  const preguntasPorTema = preguntasSnap.exists() ? preguntasSnap.val() : {};

  let temasTotal = temas.length;
  let temasImportados = 0;
  let quizzesImportados = 0;
  const errores = [];

  for (const t of temas) {
    const areaKey = areaKeyForId(t.area_id);
    const temaId = String(t.id);
    const grados = (t.grados || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    try {
      const temaRef = areaTemaDoc(areaKey, temaId);
      const existing = await getDoc(temaRef);
      const rawQs = preguntasPorTema[temaId];
      const qList = Array.isArray(rawQs)
        ? rawQs.filter(Boolean)
        : Object.values(rawQs || {}).filter(Boolean);
      const questions = qList
        .map(convertPreguntaPorTema)
        .filter((q) => q.question && q.options.length > 1);

      if (existing.exists()) {
        await updateDoc(temaRef, {
          title: t.nombre || t.title || temaId,
          areaId: t.area_id,
          grados,
          cuestionariosCount: questions.length,
          importedFromRTDB: true,
        });
      } else {
        await setDoc(temaRef, {
          title: t.nombre || t.title || temaId,
          module: '',
          areaId: t.area_id,
          grados,
          lessons: [],
          cuestionariosCount: questions.length,
          importedFromRTDB: true,
          updatedAt: serverTimestamp(),
        });
      }

      // Repartir el banco de preguntas en cuestionarios de ≤20
      const CHUNK = 20;
      for (let i = 0; i < questions.length; i += CHUNK) {
        const part = Math.floor(i / CHUNK) + 1;
        const quizId = `tema_${temaId}_parte_${part}`;
        await setDoc(areaCuestionarioDoc(areaKey, temaId, quizId), {
          id: quizId,
          title: `Examen del tema ${temaId} (parte ${part})`,
          type: 'examen',
          questions: questions.slice(i, i + CHUNK),
          updatedAt: serverTimestamp(),
        });
        quizzesImportados += 1;
      }
      temasImportados += 1;
    } catch (err) {
      errores.push(`${areaKey}/${temaId}: ${err.message}`);
    }
    if (onProgress) {
      onProgress({ areaKey, temaId, temasTotal, temasImportados, quizzesImportados });
    }
  }
  return { temasTotal, temasImportados, quizzesImportados, errores };
};

// Importa TODO el árbol especialidades_temas de RTDB a Firestore (páginas + cuestionarios).
// Es una operación de una sola vez: lee el árbol completo y escribe en Firestore.
export const importAllFromRTDB = async ({ onProgress } = {}) => {
  const meta = await get(ref(database, 'app_metadata'));
  const metadata = meta.exists() ? meta.val() : {};
  const hierarchyMap = createBidirectionalMap(metadata.grados || {});
  const corpsMap = createBidirectionalMap(metadata.cuerpos_y_servicios || {});

  const snap = await get(ref(database, 'especialidades_temas'));
  if (!snap.exists()) throw new Error('No hay datos en especialidades_temas');

  const tree = snap.val();
  let temasTotal = 0;
  let temasImportados = 0;
  let quizzesImportados = 0;
  const errores = [];

  const jerarquias = Object.keys(tree);
  for (const jCode of jerarquias) {
    const jerarquiaName = hierarchyMap.codeToName[jCode] || jCode;
    const cuerpos = Object.keys(tree[jCode] || {});
    for (const cCode of cuerpos) {
      const cuerpoName = corpsMap.codeToName[cCode] || cCode;
      const especialidades = Object.keys(tree[jCode][cCode] || {});
      for (const especialidadName of especialidades) {
        const temasObj = tree[jCode][cCode][especialidadName]?.temas || {};
        const temaIds = Object.keys(temasObj);
        temasTotal += temaIds.length;
        for (const temaId of temaIds) {
          const tema = temasObj[temaId] || {};
          const quizCount = Object.keys(tema.cuestionarios || {}).length;
          try {
            const pageRef = temaDoc(jerarquiaName, cuerpoName, especialidadName, temaId);
            const existing = await getDoc(pageRef);
            if (existing.exists()) {
              await updateDoc(pageRef, { cuestionariosCount: quizCount, importedFromRTDB: true });
            } else {
              await setDoc(pageRef, {
                title: tema.nombre || tema.title || temaId,
                module: '',
                lessons: [],
                cuestionariosCount: quizCount,
                importedFromRTDB: true,
                updatedAt: serverTimestamp(),
              });
            }
            for (const quizId of Object.keys(tema.cuestionarios || {})) {
              const quiz = convertCuestionarioRtdb(quizId, tema.cuestionarios[quizId]);
              if (quiz.questions.length) {
                await setDoc(cuestionarioDoc(jerarquiaName, cuerpoName, especialidadName, temaId, quizId), {
                  ...quiz,
                  updatedAt: serverTimestamp(),
                });
                quizzesImportados += 1;
              }
            }
            temasImportados += 1;
          } catch (err) {
            errores.push(`${jerarquiaName}/${cuerpoName}/${especialidadName}/${temaId}: ${err.message}`);
          }
          if (onProgress) {
            onProgress({ jerarquiaName, cuerpoName, especialidadName, temaId, temasTotal, temasImportados, quizzesImportados });
          }
        }
      }
    }
  }
  return { temasTotal, temasImportados, quizzesImportados, errores };
};

// Genera el contenido de una página de lectura con IA (fuente: cuestionarios o texto manual)
export const generatePaginaConIA = async ({
  jerarquia, cuerpo, especialidad, temaId, temaTitle, fuente, textoManual, userId,
  numLessons = null,   // null = la IA decide automáticamente
  targetModelKey = null,
  onProgress = () => { },
}) => {
  const page = await generatePaginaBase({
    cargarCuestionarios: async () => {
      const qs = await getCuestionarios({ jerarquia, cuerpo, especialidad, temaId });
      if (qs && qs.length) return qs;
      return await getPreguntasPorTema(temaId);
    },
    temaId,
    temaTitle,
    fuente,
    textoManual,
    userId,
    numLessons,
    targetModelKey,
    onProgress,
  });
  return { ...page, id: temaId };
};

// Igual que generatePaginaConIA pero para temas de áreas generales
// (vortexAcademyAreas/{areaKey}/temas/{temaId}).
export const generateAreaPaginaConIA = async ({
  areaKey, temaId, temaTitle, fuente, textoManual, userId,
  numLessons = null,   // null = la IA decide automáticamente
  targetModelKey = null,
  onProgress = () => { },
}) => {
  const page = await generatePaginaBase({
    cargarCuestionarios: async () => {
      const qs = await getAreaCuestionarios(areaKey, temaId);
      if (qs && qs.length) return qs;
      return await getPreguntasPorTema(temaId);
    },
    temaId,
    temaTitle,
    fuente,
    textoManual,
    userId,
    numLessons,
    targetModelKey,
    onProgress,
  });
  return { ...page, id: temaId };
};

// Núcleo compartido de generación IA para páginas de lectura individuales.
// Para textos extensos, utiliza un proceso de dos pasos que garantiza que el 100%
// del material sea cubierto sin omisiones ni truncamientos.
const generatePaginaBase = async ({
  cargarCuestionarios, temaId, temaTitle = '', fuente, textoManual, userId,
  numLessons = null,   // null/0 = la IA decide automáticamente
  targetModelKey = null,
  onProgress = () => { },
}) => {
  await vortexDocsService.initializeService();

  let contexto = '';
  if (fuente === 'cuestionarios') {
    const quizzes = await cargarCuestionarios();
    if (!quizzes || quizzes.length === 0) {
      throw new Error('Este tema aún no tiene cuestionarios ni banco de preguntas registrado. Selecciona "Desde texto manual" y pega el contenido.');
    }
    contexto = quizzes.map((q) => {
      const qTitle = q.title || `Examen del tema ${temaId}`;
      const qs = (q.questions || []).map((p) => {
        const opts = (p.options || []).join(' | ');
        const correcta = p.options?.[p.correct_index] || p.options?.[p.correctIndex] || '';
        return `- ${p.question || p.pregunta || ''}\n  Opciones: ${opts} (correcta: ${correcta})${p.explanation ? `\n  Explicación: ${p.explanation}` : ''}`;
      }).join('\n');
      return `Cuestionario "${qTitle}":\n${qs}`;
    }).join('\n\n');
  } else {
    if (!textoManual || textoManual.trim().length < 20) {
      throw new Error('Pega un texto o sube un documento con al menos 20 caracteres.');
    }
    contexto = textoManual;
  }

  const aiDecides = !numLessons || numLessons === 'auto' || parseInt(numLessons, 10) === 0;
  const lessonsCount = aiDecides ? null : Math.max(1, Math.min(10, parseInt(numLessons, 10) || 3));
  const cleanTemaName = temaTitle || temaId || 'Tema de Estudio';

  // Límite de entrada amplio para documentos extensos (hasta ~300k caracteres)
  const maxChars = 300000;
  const fullContext = contexto.length > maxChars
    ? contexto.substring(0, maxChars)
    : contexto;

  // ═══════════════════════════════════════════════════════════════════════════
  // ESTRATEGIA DE 2 PASOS EXHAUSTIVA (Para textos medianos y extensos > 2000 chars):
  // Paso 1: Partición curricular completa en MÓDULOS TEMÁTICOS y LECCIONES (15-30+ lecciones).
  // Paso 2: Generación profunda y en lotes de cada lección individualmente.
  // ═══════════════════════════════════════════════════════════════════════════
  if (fullContext.length > 2000) {
    onProgress('Analizando documento completo y estructurando módulos y lecciones...');

    const outlineCountRule = aiDecides
      ? `INSTRUCCIÓN OBLIGATORIA DE COBERTURA TOTAL:
- Analiza minuciosamente todo el documento de principio a fin.
- Agrupa el contenido en MÓDULOS TEMÁTICOS para cada gran materia o capítulo (típicamente entre 6 y 14 módulos).
- Dentro de cada módulo, crea TODAS LAS LECCIONES necesarias para cubrir cada sección, subtema, fecha histórica, unidad, escuela, tradición o normativa sin omitir nada.
- Un manual completo como este debe tener entre 12 y 35 lecciones en total (2 a 4 lecciones por módulo).
- ESTÁ ESTRICTAMENTE PROHIBIDO resumir todo en pocas lecciones o dejar capítulos fuera.`
      : `Divide el material secuencialmente en EXACTAMENTE ${lessonsCount} lecciones distribuidas en módulos temáticos coherentes, cubriendo el 100% del material.`;

    const outlineSystem = `Eres el Director de Doctrina y Educación Naval de la Armada de México.
Tu tarea es leer minuciosamente TODO el material proporcionado y crear una ESTRUCTURA CURRICULAR COMPLETA Y EXHAUSTIVA dividida en MÓDULOS TEMÁTICOS y LECCIONES DETALLADAS.

${outlineCountRule}

REGLAS DE FORMATO:
1. Divide el texto desde el primer párrafo hasta el último sin dejar nada fuera.
2. Cada lección en la lista debe tener:
   - "lessonNumber": número entero secuencial (1, 2, 3...)
   - "title": título didáctico y específico del tema (ej. "Gesta Heroica del 21 de Abril de 1914 y Héroes Navales", "Componente Operacional Marítimo: Buques de Guerra y de Estado de Derecho", etc.)
   - "module": nombre del módulo temático completo (ej. "Módulo 1: Historia Naval de México", "Módulo 2: Unidades y Medios Operativos", etc.)
   - "sections": descripción explícita y detallada de qué temas, artículos, instituciones o páginas cubre
   - "key_concepts": lista de 4 a 8 conceptos/datos clave obligatorios presentes en el texto para esa lección

Responde ÚNICAMENTE con un JSON válido con esta estructura:
{
  "title": "${cleanTemaName}",
  "totalLessons": 20,
  "lessons": [
    {
      "lessonNumber": 1,
      "title": "Consumación de la Independencia y Capitulación de San Juan de Ulúa (1821-1825)",
      "module": "Módulo 1: Historia Naval de México",
      "sections": "Consumación 1821, Iturbide primer Almirante, creación Ministerio Guerra y Marina, toma San Juan de Ulúa por Dávila, goleta Iguala 1822, bloqueo de Pedro Sainz de Baranda y capitulación 17 nov 1825",
      "key_concepts": ["Agustín de Iturbide", "Antonio de Medina", "Goleta Iguala", "Pedro Sainz de Baranda", "Capitulación San Juan de Ulúa 1825"]
    }
  ]
}`;

    const outlinePrompt = `Analiza detalladamente todo el contenido del siguiente material del tema "${cleanTemaName}" y genera la partición curricular completa en todos sus módulos y lecciones:\n\n${fullContext}`;

    const outlineRaw = await vortexDocsService.callGeminiDirect(outlinePrompt, outlineSystem, targetModelKey, 16384);
    const outlineParsed = vortexDocsService.safeJsonParse(outlineRaw);

    let plannedLessons = [];
    if (Array.isArray(outlineParsed)) {
      plannedLessons = outlineParsed;
    } else if (Array.isArray(outlineParsed?.lessons)) {
      plannedLessons = outlineParsed.lessons;
    } else if (Array.isArray(outlineParsed?.lecciones)) {
      plannedLessons = outlineParsed.lecciones;
    } else if (Array.isArray(outlineParsed?.modules) || Array.isArray(outlineParsed?.modulos)) {
      const mods = outlineParsed.modules || outlineParsed.modulos;
      mods.forEach(m => {
        const mLessons = Array.isArray(m.lessons) ? m.lessons : (Array.isArray(m.lecciones) ? m.lecciones : []);
        mLessons.forEach(l => {
          plannedLessons.push({
            ...l,
            module: l.module || m.moduleName || m.modulo || m.title || 'Módulo General'
          });
        });
      });
    } else if (typeof outlineParsed === 'object' && outlineParsed !== null) {
      for (const val of Object.values(outlineParsed)) {
        if (Array.isArray(val) && val.length > 0) {
          plannedLessons = val;
          break;
        }
      }
    }

    if (!Array.isArray(plannedLessons) || plannedLessons.length === 0) {
      throw new Error('No se pudo estructurar el plan de lecciones con la IA. Por favor intenta de nuevo.');
    }

    onProgress(`Plan curricular estructurado: ${plannedLessons.length} lecciones en ${new Set(plannedLessons.map(p => p.module)).size} módulos. Iniciando generación detallada...`);

    const lessonSystemInstruction = `Eres un experto docente naval militar de la Armada de México.
Genera UNA lección didáctica completa, rica en contenido y exhaustiva basada en la sección asignada del material.
Debes incluir todos los nombres, fechas, unidades, artículos, escuelas, tradiciones y doctrinas correspondientes.
NO inventes información ni omitas datos que estén en el texto asignado.

Responde ÚNICAMENTE con el objeto JSON de la lección (sin array externo, sin texto adicional):
{
  "title": "Título didáctico de la lección",
  "module": "Módulo correspondiente",
  "paragraphs": [
    "Párrafo 1 detallado explicando la doctrina, historia o normativa...",
    "Párrafo 2 con fundamentos y explicaciones técnicas...",
    "Párrafo 3 con aplicación práctica u operativa...",
    "Párrafo 4 con detalles normativos o de procedimiento..."
  ],
  "bullets": [
    "Concepto normativo 1 extraído del texto",
    "Concepto normativo 2 extraído del texto",
    "Concepto normativo 3 extraído del texto",
    "Concepto normativo 4 extraído del texto"
  ],
  "callout": {
    "type": "info",
    "icon": "⚓",
    "text": "Nota técnica, operativa, histórica o de seguridad fundamental extraída del material..."
  },
  "kps": [
    { "t": "¿Punto de evaluación 1?", "b": "Respuesta clara y fundamentada..." },
    { "t": "¿Punto de evaluación 2?", "b": "Respuesta clara y fundamentada..." }
  ],
  "quizQuestions": [
    {
      "question": "¿Pregunta de autoevaluación 1 sobre este contenido?",
      "options": ["Opción Correcta", "Distractor 1", "Distractor 2", "Distractor 3"],
      "correct_index": 0,
      "explanation": "Explicación doctrinal basada en el material."
    },
    {
      "question": "¿Pregunta de autoevaluación 2 sobre este contenido?",
      "options": ["Opción Correcta", "Distractor 1", "Distractor 2", "Distractor 3"],
      "correct_index": 0,
      "explanation": "Explicación doctrinal basada en el material."
    }
  ],
  "flashcards": [
    { "front": "Concepto clave para memorizar", "back": "Definición y aplicación exacta" },
    { "front": "Fundamento normativo", "back": "Respuesta resumida" }
  ],
  "video_url": "",
  "audio_url": "",
  "audio_caption": "",
  "infographic_url": "",
  "slides_url": "",
  "report_url": "",
  "pdf_resources": [],
  "interactive": true,
  "isQuiz": false
}

REGLAS: Español castrense. 3 a 5 párrafos ricos y sustanciales. 3 a 5 bullets. 2 a 3 kps. 2 a 3 quizQuestions (4 opciones). 2 a 4 flashcards. Solo JSON válido.`;

    // Generación por lotes de 4 para no saturar y mantener actualización en vivo
    const validLessons = [];
    const BATCH_SIZE = 4;

    for (let i = 0; i < plannedLessons.length; i += BATCH_SIZE) {
      const batch = plannedLessons.slice(i, i + BATCH_SIZE);
      const batchPromises = batch.map(async (plan, bIdx) => {
        const globalIdx = i + bIdx;
        const lessonPrompt = `Genera la Lección ${globalIdx + 1}: "${plan.title}" (${plan.module || `Módulo ${globalIdx + 1}`}) del tema "${cleanTemaName}".

SECCIÓN ESPECÍFICA QUE DEBES DESARROLLAR A FONDO EN ESTA LECCIÓN:
${plan.sections}

CONCEPTOS CLAVE OBLIGATORIOS QUE DEBES INCLUIR:
${(plan.key_concepts || []).join(', ')}

MATERIAL COMPLETO DE REFERENCIA (Extrae y desarrolla detalladamente lo correspondiente a esta lección sin omitir nada):
${fullContext}`;

        try {
          const raw = await vortexDocsService.callGeminiDirect(lessonPrompt, lessonSystemInstruction, targetModelKey, 8192);
          const parsed = vortexDocsService.safeJsonParse(raw);

          if (!parsed) return null;
          const lessonObj = Array.isArray(parsed) ? parsed[0] : (parsed.lessons ? parsed.lessons[0] : parsed);
          if (!lessonObj) return null;

          return {
            ...lessonObj,
            title: lessonObj.title || plan.title || `Lección ${globalIdx + 1}`,
            module: lessonObj.module || plan.module || `Módulo ${globalIdx + 1}`,
          };
        } catch (err) {
          console.warn(`Error generando lección individual ${globalIdx + 1}:`, err);
          return null;
        }
      });

      const batchResults = await Promise.all(batchPromises);
      batchResults.forEach(r => { if (r) validLessons.push(r); });
      onProgress(`Generadas ${validLessons.length} de ${plannedLessons.length} lecciones...`);
    }

    if (validLessons.length === 0) {
      throw new Error('No se pudieron generar las lecciones con la IA. Por favor intenta de nuevo.');
    }

    const normalizedLessons = validLessons.map((l, idx) => {
      const norm = normalizeLesson(l);
      norm.id = norm.id || `${temaId}_l${idx + 1}`;
      norm.module = norm.module || outlineParsed?.module || `Módulo ${idx + 1}`;
      return norm;
    });

    return {
      title: outlineParsed?.title || cleanTemaName,
      module: outlineParsed?.module || '',
      lessons: normalizedLessons,
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ESTRATEGIA DIRECTA (Para textos muy breves <= 2000 caracteres)
  // ═══════════════════════════════════════════════════════════════════════════
  onProgress('Generando lecciones con IA...');

  const lessonsInstruction = aiDecides
    ? `Decide cuántas lecciones son necesarias (mínimo 2, máximo 6) para cubrir TODO el contenido del material sin omitir nada.`
    : `Genera EXACTAMENTE ${lessonsCount} lecciones divididas por módulos coherentes.`;

  const systemInstruction = `Eres un experto docente naval militar de la Armada de México.
Tu tarea es transformar el material de estudio en lecciones completas y didácticas para el tema "${cleanTemaName}".
${lessonsInstruction}

IMPORTANTE: Debes utilizar TODA la información proporcionada. No omitas secciones ni conceptos presentes en el material.

Responde ÚNICAMENTE con un objeto JSON válido con esta estructura:
{
  "title": "${cleanTemaName}",
  "module": "Módulo General",
  "lessons": [
    {
      "title": "Lección 1: Título",
      "module": "Módulo 1",
      "paragraphs": ["Párrafo 1...", "Párrafo 2...", "Párrafo 3..."],
      "bullets": ["Concepto 1", "Concepto 2", "Concepto 3"],
      "callout": { "type": "info", "icon": "⚓", "text": "Nota..." },
      "kps": [{ "t": "¿Punto 1?", "b": "Respuesta 1" }],
      "quizQuestions": [{ "question": "¿Pregunta?", "options": ["A", "B", "C", "D"], "correct_index": 0, "explanation": "..." }],
      "flashcards": [{ "front": "Frente", "back": "Reverso" }],
      "video_url": "", "audio_url": "", "audio_caption": "", "infographic_url": "", "slides_url": "", "report_url": "", "pdf_resources": [],
      "interactive": true, "isQuiz": false
    }
  ]
}`;

  const prompt = `Genera las lecciones estructuradas completas cubriendo TODO el siguiente material del tema "${cleanTemaName}":\n\n${fullContext}`;

  const raw = await vortexDocsService.callGeminiDirect(prompt, systemInstruction, targetModelKey, 8192);
  const parsed = vortexDocsService.safeJsonParse(raw);
  if (!parsed) throw new Error('No se pudo interpretar la respuesta JSON de la IA.');

  const page = parsed.lessons
    ? parsed
    : (Array.isArray(parsed) ? { title: cleanTemaName, module: '', lessons: parsed } : null);
  if (!page) throw new Error('La IA devolvió un formato no esperado.');

  const lessonsList = Array.isArray(page.lessons) ? page.lessons : Object.values(page.lessons || {});
  const normalizedLessons = lessonsList.map((l, idx) => {
    const norm = normalizeLesson(l);
    norm.id = norm.id || `${temaId}_l${idx + 1}`;
    norm.module = norm.module || page.module || `Módulo ${idx + 1}`;
    return norm;
  });

  return {
    title: page.title || cleanTemaName,
    module: page.module || '',
    lessons: normalizedLessons,
  };
};

// Genera múltiples temas con sus respectivas lecciones estructuradas a partir de un documento o texto fuente.
export const generateTemasYLeccionesConIA = async ({
  documentoTexto,
  numTemas = 3,
  leccionesPorTema = 3,
  areaKey = 'pensamiento_matematico',
  grados = [],
  userId,
  targetModelKey = null,
  onProgress = () => { },
}) => {
  if (!documentoTexto || documentoTexto.trim().length < 50) {
    throw new Error('El documento o texto debe tener al menos 50 caracteres para poder estructurar temas y lecciones.');
  }

  onProgress({ step: 'Inicializando conexión con el modelo de IA...', progress: 10 });
  await vortexDocsService.initializeService();

  const areaLabel = AREA_NAMES[areaKey] || areaKey;
  const targetTemasCount = Math.max(1, Math.min(10, parseInt(numTemas, 10) || 3));
  const targetLessonsCount = Math.max(1, Math.min(6, parseInt(leccionesPorTema, 10) || 3));

  onProgress({ step: `Analizando documento y estructurando ${targetTemasCount} temas con ${targetLessonsCount} lecciones c/u...`, progress: 30 });

  const systemInstruction = `Eres un experto catedrático naval de la Armada de México y diseñador curricular militar de alto nivel.
Tu misión es transformar el documento o material de estudio proporcionado en un plan de estudios completo y estructurado para el área "${areaLabel}".

Debes generar EXACTAMENTE ${targetTemasCount} temas.
Para cada tema, debes generar EXACTAMENTE ${targetLessonsCount} lecciones completas y didácticas.

Responde ÚNICAMENTE con un objeto JSON válido, sin delimitadores extraños ni explicaciones antes o después.
Estructura EXACTA requerida:
{
  "temas": [
    {
      "id": "tema_1_identificador_slug",
      "title": "Tema 1: Título completo del Tema",
      "module": "Módulo 1",
      "grados": ${JSON.stringify(grados)},
      "lessons": [
        {
          "title": "Lección 1.1: Título didáctico de la lección",
          "module": "Módulo 1",
          "paragraphs": [
            "Párrafo explicativo detallado con fundamentos técnicos o normativos...",
            "Párrafo secundario con aplicación práctica y operativa..."
          ],
          "bullets": [
            "Concepto clave y normativo 1",
            "Concepto clave y normativo 2",
            "Concepto clave y normativo 3"
          ],
          "callout": {
            "type": "info",
            "icon": "⚓",
            "text": "Nota importante u obligatoria para el personal naval..."
          },
          "kps": [
            { "t": "¿Cuál es el propósito principal?", "b": "Explicación concisa y directa..." },
            { "t": "¿Qué normativa rige esta materia?", "b": "Fundamento legal y directiva aplicable..." }
          ],
          "quizQuestions": [
            {
              "question": "¿Pregunta de evaluación sobre el contenido de la lección?",
              "options": [
                "Opción correcta y fundamentada",
                "Opción distractora creíble 1",
                "Opción distractora 2",
                "Opción distractora 3"
              ],
              "correct_index": 0,
              "explanation": "Justificación doctrinal de la respuesta correcta."
            }
          ],
          "flashcards": [
            { "front": "Concepto o término naval", "back": "Definición y aplicación operativa" },
            { "front": "Criterio de evaluación", "back": "Respuesta clave de estudio" }
          ],
          "video_url": "",
          "audio_url": "",
          "audio_caption": "",
          "infographic_url": "",
          "slides_url": "",
          "report_url": "",
          "pdf_resources": [],
          "interactive": true,
          "isQuiz": false
        }
      ]
    }
  ]
}

REGLAS ESTRICTAS:
1. TODO en idioma Español, lenguaje formal, castrense y didáctico.
2. Cada tema debe tener un ID único y legible (letras minúsculas, números y guiones bajos).
3. Cada lección debe tener contenido sustancial y real derivado del texto fuente.
4. "paragraphs": mínimo 2 párrafos bien redactados por lección.
5. "bullets": 3 a 5 puntos clave.
6. "quizQuestions": 2 a 3 preguntas con 4 opciones cada una y un correct_index numérico (0 a 3).
7. "flashcards": 2 a 4 tarjetas de estudio por lección.
8. Genera JSON estrictamente válido, sin comas finales ni texto introductorio.`;

  // Limitar longitud del texto si excede el límite razonable del prompt
  const maxChars = 45000;
  const trimmedText = documentoTexto.length > maxChars
    ? documentoTexto.substring(0, maxChars) + "\n\n[... CONTENIDO ADICIONAL SINTETIZADO ...]"
    : documentoTexto;

  const prompt = `Analiza el siguiente texto y genera la estructura completa de ${targetTemasCount} temas y ${targetLessonsCount} lecciones por tema para el área "${areaLabel}":\n\n${trimmedText}`;

  onProgress({ step: 'Enviando petición a la IA y procesando contenido...', progress: 50 });

  const raw = await vortexDocsService.callOpenRouterAPI(
    prompt,
    userId || 'admin',
    'generacion_temas_lecciones',
    systemInstruction,
    targetModelKey
  );

  if (!raw) {
    throw new Error('La IA no devolvió respuesta.');
  }

  onProgress({ step: 'Parseando y validando estructura de temas y lecciones...', progress: 85 });

  const parsed = vortexDocsService.safeJsonParse(raw);
  if (!parsed) {
    throw new Error('No se pudo parsear el JSON generado por el modelo de IA. Inténtalo de nuevo.');
  }

  const rawTemas = Array.isArray(parsed) ? parsed : (parsed.temas || parsed.topics || [parsed]);
  if (!rawTemas || !rawTemas.length) {
    throw new Error('La IA devolvió una estructura sin temas válidos.');
  }

  const normalizedTemas = rawTemas.map((t, tIdx) => {
    const rawId = t.id || `tema_${Date.now()}_${tIdx + 1}`;
    const cleanId = String(rawId)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9_-]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '') || `tema_${tIdx + 1}`;

    const rawLessons = t.lessons || t.lecciones || [];
    const lessonsList = Array.isArray(rawLessons) ? rawLessons : Object.values(rawLessons);

    const normalizedLessons = lessonsList.map((l, lIdx) => {
      const norm = normalizeLesson(l);
      norm.id = norm.id || `${cleanId}_l${lIdx + 1}`;
      norm.module = norm.module || t.module || `Módulo ${tIdx + 1}`;
      return norm;
    });

    return {
      id: cleanId,
      title: t.title || t.nombre || `Tema ${tIdx + 1}`,
      module: t.module || `Módulo ${tIdx + 1}`,
      areaId: areaKey,
      grados: Array.isArray(t.grados) && t.grados.length > 0 ? t.grados : (grados.length > 0 ? grados : []),
      lessons: normalizedLessons,
      cuestionariosCount: normalizedLessons.reduce((acc, l) => acc + (l.quizQuestions?.length ? 1 : 0), 0),
      multimedia: null,
    };
  });

  onProgress({ step: `¡Generación completada! ${normalizedTemas.length} temas listos para guardar.`, progress: 100 });
  return normalizedTemas;
};

// Guarda una lista de temas generados directamente en Firestore
export const saveGeneratedTemasToFirestore = async ({
  areaKey,
  jerarquia,
  cuerpo,
  especialidad,
  temasList = [],
  onProgress = () => { },
}) => {
  if (!temasList || !temasList.length) return { guardados: 0, errores: [] };

  const errores = [];
  let guardados = 0;

  for (let i = 0; i < temasList.length; i++) {
    const tema = temasList[i];
    const temaId = tema.id;

    onProgress({
      current: i + 1,
      total: temasList.length,
      step: `Guardando tema "${tema.title}" (${i + 1}/${temasList.length})...`,
    });

    try {
      if (areaKey) {
        await saveAreaTema({ areaKey, temaId }, tema);
      } else if (jerarquia && cuerpo && especialidad) {
        await saveTema({ jerarquia, cuerpo, especialidad, temaId }, tema);
      } else {
        throw new Error('Falta especificar el área o la especialidad de destino.');
      }
      guardados++;
    } catch (err) {
      errores.push(`Error en "${tema.title}": ${err.message}`);
    }
  }

  return { guardados, errores };
};

// Genera el contenido de un tema (libro) de INGLÉS con IA.
// A diferencia de generatePaginaConIA (español castrense naval), este perfil
// produce lecciones bilingües: regla explicada en español, ejemplos y drills en
// inglés, y flashcards de vocabulario. Comparte la forma de return
// { title, module, lessons } para integrarse en AdminLectorTemas sin cambios.
export const generateInglesPaginaConIA = async ({
  temaId,
  temaTitle = '',
  fuente = 'texto',
  textoManual = '',
  userId,
  numLessons = null,
  targetModelKey = null,
  onProgress = () => { },
}) => {
  await vortexDocsService.initializeService();

  const cleanTemaName = temaTitle || temaId || 'Inglés';

  // La fuente del material puede ser texto pegado (ejercicios/reglas del libro)
  // o el título del punto gramatical (la IA lo desarrolla desde su conocimiento).
  if (fuente === 'texto') {
    if (!textoManual || textoManual.trim().length < 20) {
      throw new Error('Pega el texto del punto gramatical o selecciona "Desde tema gramatical" para que la IA lo desarrolle.');
    }
  } else {
    if (!cleanTemaName || cleanTemaName.length < 3) {
      throw new Error('Indica un tema gramatical válido (ej. "Demonstratives: this / that / these / those").');
    }
  }

  const aiDecides = !numLessons || numLessons === 'auto' || parseInt(numLessons, 10) === 0;
  const lessonsCount = aiDecides ? null : Math.max(1, Math.min(8, parseInt(numLessons, 10) || 1));
  const maxChars = 300000;
  const fullContext = (textoManual || '').length > maxChars
    ? textoManual.substring(0, maxChars)
    : textoManual || '';

  onProgress('Analizando el punto gramatical y estructurando la lección...');

  // ── Paso 1: desglose del punto gramatical en una o varias lecciones ──
  const outlineCountRule = aiDecides
    ? `INSTRUCCIÓN DE COBERTURA:
- Analiza el punto gramatical de principio a fin.
- Si el texto cubre varios subpuntos gramaticales, crea UNA lección por subpunto coherente (máximo 6).
- Si es un único punto, crea UNA sola lección completa.`
    : `Divide el material en EXACTAMENTE ${lessonsCount} lecciones coherentes, sin omitir nada.`;

  const outlineSystem = `Eres un profesor experto de inglés americano (American English Course, niveles I y II)
que diseña contenido interactivo para adultos hispanohablantes.

Tu tarea es leer el material proporcionado y desglosarlo en una o más lecciones
de gramática, cada una enfocada en UN punto concreto.

${outlineCountRule}

Responde ÚNICAMENTE con un JSON válido con esta estructura:
{
  "title": "${cleanTemaName}",
  "lessons": [
    {
      "lessonNumber": 1,
      "title": "Nombre del punto gramatical",
      "module": "Level I - Book X",
      "sections": "Descripción del punto: regla, usos y ejemplos a desarrollar",
      "key_concepts": ["concepto 1", "concepto 2", "concepto 3"]
    }
  ]
}`;

  const outlinePrompt = buildInglesPrompt(fuente, cleanTemaName, fullContext, 'outline');

  const outlineRaw = await vortexDocsService.callGeminiDirect(outlinePrompt, outlineSystem, targetModelKey, 16384);
  const outlineParsed = vortexDocsService.safeJsonParse(outlineRaw);

  let plannedLessons = [];
  if (Array.isArray(outlineParsed)) {
    plannedLessons = outlineParsed;
  } else if (Array.isArray(outlineParsed?.lessons)) {
    plannedLessons = outlineParsed.lessons;
  } else if (Array.isArray(outlineParsed?.lecciones)) {
    plannedLessons = outlineParsed.lecciones;
  }

  if (!Array.isArray(plannedLessons) || plannedLessons.length === 0) {
    // Si la IA no devolvió desglose, cae en una sola lección.
    plannedLessons = [{ lessonNumber: 1, title: cleanTemaName, module: 'Inglés', sections: cleanTemaName, key_concepts: [] }];
  }

  onProgress(`Plan estructurado: ${plannedLessons.length} lección(es). Generando detalle...`);

  // ── Paso 2: generación de cada lección con el formato Vortex Academy ──
  const lessonSystemInstruction = `Eres un profesor experto de inglés americano (American English Course, niveles I y II)
que crea lecciones interactivas para personal adulto hispanohablante.

Formato de CADA lección (devuelve ÚNICAMENTE el objeto JSON de la lección, sin array externo ni texto adicional):
{
  "title": "Punto gramatical",
  "module": "Level correspondiente",
  "paragraphs": [
    "Párrafo 1: la REGLA gramatical explicada en ESPAÑOL claro y breve, con ejemplos en INGLÉS y su traducción entre paréntesis.",
    "Párrafo 2: uso y contexto, ejemplos en INGLÉS con traducción.",
    "Párrafo 3: errores comunes o matices, ejemplos en INGLÉS con traducción."
  ],
  "bullets": [
    "Regla rápida 1 (español breve) + ejemplo en inglés",
    "Regla rápida 2 + ejemplo en inglés",
    "Regla rápida 3 + ejemplo en inglés"
  ],
  "callout": {
    "type": "info",
    "icon": "🇺🇸",
    "text": "Error común o tip de pronunciación (español) + ejemplo en inglés."
  },
  "kps": [
    { "t": "¿Punto clave 1?", "b": "Respuesta breve en inglés con traducción" },
    { "t": "¿Punto clave 2?", "b": "Respuesta breve en inglés con traducción" }
  ],
  "quizQuestions": [
    {
      "question": "Fill in the blank: She ___ a student.",
      "options": ["is", "are", "am", "be"],
      "correct_index": 0,
      "explanation": "Explicación de la respuesta en ESPAÑOL."
    }
  ],
  "flashcards": [
    { "front": "palabra o frase en inglés", "back": "traducción al español" }
  ],
  "video_url": "",
  "audio_url": "",
  "audio_caption": "Frase de ejemplo ideal para grabar audio de pronunciación",
  "infographic_url": "",
  "slides_url": "",
  "report_url": "",
  "pdf_resources": [],
  "interactive": true,
  "isQuiz": false
}

REGLAS ESTRICTAS:
- 3 a 5 párrafos ricos y sustanciales (regla en español, ejemplos en inglés).
- 3 a 5 bullets.
- 2 a 3 kps.
- 5 a 8 quizQuestions tipo "fill in the blank" o transformación, con 4 opciones y correct_index (0-3).
- 4 a 8 flashcards de vocabulario (front inglés, back español).
- "question" y "options" SIEMPRE en INGLÉS; "explanation" y la regla en ESPAÑOL.
- No inventes vocabulario ajeno al tema. Solo JSON válido, sin texto antes ni después.`;

  const BATCH_SIZE = 4;
  const validLessons = [];

  for (let i = 0; i < plannedLessons.length; i += BATCH_SIZE) {
    const batch = plannedLessons.slice(i, i + BATCH_SIZE);
    const batchPromises = batch.map(async (plan, bIdx) => {
      const globalIdx = i + bIdx;
      const lessonPrompt = `Desarrolla a fondo la Lección ${globalIdx + 1}: "${plan.title}" (${plan.module || 'Inglés'}).

PUNTO GRAMATICAL A DESARROLLAR:
${plan.sections || plan.title}

CONCEPTOS CLAVE:
${(plan.key_concepts || []).join(', ')}

${fuente === 'texto' ? `MATERIAL DE REFERENCIA DEL LIBRO (extrae la regla y los ejemplos sin omitir nada):\n${fullContext}` : `Desarrolla este punto gramatical desde tu conocimiento profesional del inglés americano.`}`;

      try {
        const raw = await vortexDocsService.callGeminiDirect(lessonPrompt, lessonSystemInstruction, targetModelKey, 8192);
        const parsed = vortexDocsService.safeJsonParse(raw);
        if (!parsed) return null;
        const lessonObj = Array.isArray(parsed) ? parsed[0] : (parsed.lessons ? parsed.lessons[0] : parsed);
        if (!lessonObj) return null;
        return {
          ...lessonObj,
          title: lessonObj.title || plan.title || `Lección ${globalIdx + 1}`,
          module: lessonObj.module || plan.module || 'Inglés',
        };
      } catch (err) {
        console.warn(`Error generando lección de inglés ${globalIdx + 1}:`, err);
        return null;
      }
    });

    const batchResults = await Promise.all(batchPromises);
    batchResults.forEach((r) => { if (r) validLessons.push(r); });
    onProgress(`Generadas ${validLessons.length} de ${plannedLessons.length} lecciones...`);
  }

  if (validLessons.length === 0) {
    throw new Error('No se pudieron generar las lecciones con la IA. Intenta de nuevo.');
  }

  const normalizedLessons = validLessons.map((l, idx) => {
    const norm = normalizeLesson(l);
    norm.id = norm.id || `${temaId || 'ingles'}_l${idx + 1}`;
    norm.module = norm.module || outlineParsed?.module || 'Inglés';
    return norm;
  });

  return {
    title: outlineParsed?.title || cleanTemaName,
    module: 'Inglés',
    lessons: normalizedLessons,
  };
};

// Construye el prompt de entrada según la fuente (texto o tema gramatical).
function buildInglesPrompt(fuente, temaName, contexto, fase) {
  if (fase === 'outline') {
    if (fuente === 'texto') {
      return `Desglosa el siguiente material del tema "${temaName}" en lecciones, cubriendo cada punto gramatical sin omitir nada:\n\n${contexto}`;
    }
    return `Desglosa el punto gramatical "${temaName}" en una o más lecciones coherentes.`;
  }
  return '';
}

// Verifica que el usuario actual tiene permiso de escritura en Firestore
// (colección admins/{uid} — la misma que usan las reglas del sistema).
export const getFirestoreAdminStatus = async (uid) => {
  if (!uid) return false;
  try {
    const snap = await getDoc(doc(db, 'admins', uid));
    return snap.exists();
  } catch (e) {
    console.warn('[AcademyFirestore] No se pudo verificar admin Firestore:', e);
    return false;
  }
};


// ── Puente banco RTDB /modulos → lector de cursos transversales EXANI-II ──
// Los 4 módulos transversales del banco real (pensamiento_matematico,
// comprension_lectora, redaccion_indirecta, ingles) viven en RTDB bajo
// `/modulos/{key}` con el MISMO key que `areaKey` del catálogo. Cuando el
// área aún no tiene temas en Firestore (vortexAcademyAreas), el lector usa
// las funciones `ConBanco` para construir en vivo el tema-banco y sus
// cuestionarios (chunks ≤ 20 reactivos) directamente desde la RTDB.
import {
  buildTemaDesdeModulo,
  buildCuestionariosDesdeModulo,
  MODULOS_TRANSVERSALES_BANCO,
} from './modulosBankService';

/** Devuelve los temas del área; si Firestore está vacío y el área es un
 *  módulo transversal con banco en RTDB, expone el tema-banco real. */
export const getAreaTemasConBanco = async (areaKey) => {
  const firestoreTemas = await getAreaTemas(areaKey);
  if (firestoreTemas?.length > 0) return firestoreTemas;
  const moduloDesc = MODULOS_TRANSVERSALES_BANCO[areaKey];
  if (!moduloDesc) return firestoreTemas;
  try {
    const tema = await buildTemaDesdeModulo(areaKey, moduloDesc);
    return tema ? [tema] : firestoreTemas;
  } catch (e) {
    console.warn('[AcademyFirestore] Fallback banco módulo RTDB:', areaKey, e?.message);
    return firestoreTemas;
  }
};

/** Devuelve los cuestionarios de un tema; para temas sintéticos `banco_*`
 *  construye los mini-exámenes en vivo desde el banco RTDB del módulo. */
export const getAreaCuestionariosConBanco = async (areaKey, temaId) => {
  const temaEsBanco = String(temaId || '').startsWith('banco_');
  if (!temaEsBanco) return getAreaCuestionarios(areaKey, temaId);
  const moduloDesc = MODULOS_TRANSVERSALES_BANCO[areaKey];
  if (!moduloDesc) return [];
  try {
    const quizzes = await buildCuestionariosDesdeModulo(areaKey, moduloDesc);
    return Array.isArray(quizzes) ? quizzes : [];
  } catch (e) {
    console.warn('[AcademyFirestore] Fallback cuestionarios banco RTDB:', areaKey, e?.message);
    return [];
  }
};
